import {useMutation, useQueryClient} from '@tanstack/react-query';
import {BillingBlock, BillingBlockExemptionCreate, DelinquencySettingsUpdate} from 'src/core/data/api/BillingBlock';
import BillingService from 'src/services/billing-service';
import {getCurrentCustomerId} from 'src/helpers/customer';
import {billingBlockKeys} from './queries';
import {errorMessageId} from './errors';

export const useUpdateDelinquencySettings = () => {
  const service = new BillingService();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({graceDays, blockVehicles}: DelinquencySettingsUpdate) => (
      service.updateDelinquencySettings(getCurrentCustomerId(), {graceDays, blockVehicles})
    ),
    // Turning blocking off releases every active block on the server.
    onSettled: () => Promise.all([
      queryClient.invalidateQueries({queryKey: billingBlockKeys.settings()}),
      queryClient.invalidateQueries({queryKey: billingBlockKeys.blocks()}),
    ]),
  });
};

export const useReleaseBlock = () => {
  const service = new BillingService();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({id, note}: {id: number; note: string}) => service.releaseBlock(getCurrentCustomerId(), id, note.trim()),
    onSettled: () => queryClient.invalidateQueries({queryKey: billingBlockKeys.blocks()}),
  });
};

// An exemption releases the active blocks it covers, so both lists change.
const invalidateBlocksAndExemptions = (queryClient: ReturnType<typeof useQueryClient>) => Promise.all([
  queryClient.invalidateQueries({queryKey: billingBlockKeys.blocks()}),
  queryClient.invalidateQueries({queryKey: billingBlockKeys.exemptions()}),
]);

export const useCreateBlockExemption = () => {
  const service = new BillingService();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: BillingBlockExemptionCreate) => service.createBlockExemption(getCurrentCustomerId(), body),
    onSettled: () => invalidateBlocksAndExemptions(queryClient),
  });
};

export interface ReleaseAndExemptInput {
  block: BillingBlock;
  note: string;
  target: 'device' | 'customer';
}

export type ReleaseAndExemptResult =
  | {exempted: true}
  | {exempted: false; error: unknown}
  | {exempted: false; released: number; total: number};

// The server has no combined endpoint: release first, then exempt, and report a half-done result.
// Exempting a customer marks its other active blocks as released without sending the command, so
// they are released here first; if any of them stays blocked, the exemption is not created.
export const useReleaseAndExempt = () => {
  const service = new BillingService();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({block, note, target}: ReleaseAndExemptInput): Promise<ReleaseAndExemptResult> => {
      const trimmed = note.trim();
      const owner = getCurrentCustomerId();
      await service.releaseBlock(owner, block.id, trimmed);
      if (target === 'customer') {
        const active: BillingBlock[] = await service.listBlocks(owner, {status: 'ACTIVE', customerId: block.customerId, deviceId: undefined});
        const others = (active || []).filter((other) => other.id !== block.id);
        const results = await Promise.allSettled(others.map((other) => service.releaseBlock(owner, other.id, trimmed)));
        const released = results.filter((result) => (
          result.status === 'fulfilled' || errorMessageId(result.reason) === 'billingBlockErrorAlreadyReleased'
        )).length;
        if (released < others.length) return {exempted: false, released: released + 1, total: others.length + 1};
      }
      try {
        await service.createBlockExemption(owner, target === 'device' ?
          {deviceId: block.deviceId, note: trimmed} :
          {customerId: block.customerId, note: trimmed});
        return {exempted: true};
      } catch (error) {
        return {exempted: false, error};
      }
    },
    onSettled: () => invalidateBlocksAndExemptions(queryClient),
  });
};

export const useRemoveBlockExemption = () => {
  const service = new BillingService();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => service.removeBlockExemption(getCurrentCustomerId(), id),
    onSettled: () => queryClient.invalidateQueries({queryKey: billingBlockKeys.exemptions()}),
  });
};
